import { useState } from "react";
import { Eye, EyeOff, Plus, UserCog, X } from "lucide-react";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import type * as API from "@shared-core/data/api-types";

export type TicketPriority = "low" | "normal" | "high" | "urgent";

export const PRIORITY_CONFIG: Record<
  TicketPriority,
  { label: string; variant: "neutral" | "outline" | "warning" | "error" }
> = {
  low: { label: "Low", variant: "neutral" },
  normal: { label: "Normal", variant: "outline" },
  high: { label: "High", variant: "warning" },
  urgent: { label: "Urgent", variant: "error" },
};

export type TicketStatusValue =
  "open" | "in_progress" | "waiting" | "resolved" | "closed";

export const STATUS_CONFIG: Record<
  TicketStatusValue,
  {
    label: string;
    variant: "outline" | "warning" | "secondary" | "success" | "neutral";
  }
> = {
  open: { label: "Open", variant: "outline" },
  in_progress: { label: "In Progress", variant: "warning" },
  waiting: { label: "Waiting", variant: "secondary" },
  resolved: { label: "Resolved", variant: "success" },
  closed: { label: "Closed", variant: "neutral" },
};

export interface AssignableUser {
  id: string;
  name: string;
}

const NO_CATEGORY_VALUE = "__none__";
const UNASSIGNED_VALUE = "__unassigned__";

interface TicketMetadataStripProps {
  status: TicketStatusValue;
  priority: TicketPriority;
  categoryId: string | null;
  categories: API.TicketCategory[];
  tags: API.TicketTag[];
  isWatching: boolean;
  /** True when the current user is the ticket's requester — an implicit,
   * non-removable watch, so the toggle is shown but not interactive. */
  isImplicitWatcher?: boolean;
  otherWatcherNames: string[];
  assignedToUserId: string | null;
  assignableUsers: AssignableUser[];
  /** Resolves a user id to a display name; falls back to the raw id
   * when the id isn't in `assignableUsers` (e.g. a stale/removed user). */
  resolveUserName: (userId: string) => string;
  editable?: boolean;
  /** Statuses the ticket refuses now, each with its reason (a closed ticket: "Ticket closed: reopen the ticket first."), from the
   * ticket record's commands; shown disabled with the reason. */
  unavailableStatuses?: Partial<Record<TicketStatusValue, string>>;
  /** Statuses whose command does not apply to the ticket now (hide or block): not offered at all. */
  hiddenStatuses?: TicketStatusValue[];
  onChangeStatus: (status: TicketStatusValue) => void;
  onChangePriority: (priority: TicketPriority) => void;
  onChangeCategory: (categoryId: string | null) => void;
  onAddTag: (tagValue: string) => void;
  onRemoveTag: (tagId: string) => void;
  onToggleWatch?: () => void;
  onReassign?: (userId: string) => void;
}

export function TicketMetadataStrip({
  status,
  priority,
  categoryId,
  categories,
  tags,
  isWatching,
  isImplicitWatcher = false,
  otherWatcherNames,
  assignedToUserId,
  assignableUsers,
  resolveUserName,
  editable = true,
  unavailableStatuses = {},
  hiddenStatuses = [],
  onChangeStatus,
  onChangePriority,
  onChangeCategory,
  onAddTag,
  onRemoveTag,
  onToggleWatch,
  onReassign,
}: TicketMetadataStripProps) {
  const [newTag, setNewTag] = useState("");
  const [showTagInput, setShowTagInput] = useState(false);

  const handleAddTag = () => {
    const value = newTag.trim();
    if (!value) return;
    onAddTag(value);
    setNewTag("");
    setShowTagInput(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3 border-b border-border text-sm">
      {/* Status — directly settable here (mirrors priority/category), rather
          than only via the binary Mark as Resolved / Reopen actions. "Closed"
          is not offered: people stop at Resolved and the quiet-period sweep
          closes it. An already-closed ticket shows it as a disabled item so
          the trigger still renders its label. */}
      <div className="flex items-center gap-1.5">
        <span className="text-muted-foreground">Status</span>
        {editable ? (
          <Select
            value={status}
            onValueChange={(v) => onChangeStatus(v as TicketStatusValue)}
          >
            <SelectTrigger className="h-7 text-sm w-[130px]" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(STATUS_CONFIG) as TicketStatusValue[])
                .filter((s) => s !== "closed" || status === "closed")
                .filter((s) => s === status || !hiddenStatuses.includes(s))
                .map((s) => {
                  const reason = unavailableStatuses[s];
                  return (
                    <SelectItem
                      key={s}
                      value={s}
                      disabled={s === "closed" || reason !== undefined}
                      title={reason}
                    >
                      {STATUS_CONFIG[s].label}
                      {reason && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          {reason}
                        </span>
                      )}
                    </SelectItem>
                  );
                })}
            </SelectContent>
          </Select>
        ) : (
          <Badge variant={STATUS_CONFIG[status].variant}>
            {STATUS_CONFIG[status].label}
          </Badge>
        )}
      </div>

      {/* Priority */}
      <div className="flex items-center gap-1.5">
        <span className="text-muted-foreground">Priority</span>
        {editable ? (
          <Select
            value={priority}
            onValueChange={(v) => onChangePriority(v as TicketPriority)}
          >
            <SelectTrigger className="h-7 text-sm w-[110px]" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(PRIORITY_CONFIG) as TicketPriority[]).map((p) => (
                <SelectItem key={p} value={p}>
                  {PRIORITY_CONFIG[p].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Badge variant={PRIORITY_CONFIG[priority].variant}>
            {PRIORITY_CONFIG[priority].label}
          </Badge>
        )}
      </div>

      {/* Category */}
      <div className="flex items-center gap-1.5">
        <span className="text-muted-foreground">Category</span>
        {editable ? (
          <Select
            value={categoryId ?? NO_CATEGORY_VALUE}
            onValueChange={(v) =>
              onChangeCategory(v === NO_CATEGORY_VALUE ? null : v)
            }
          >
            <SelectTrigger className="h-7 text-sm w-[160px]" size="sm">
              <SelectValue placeholder="No category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_CATEGORY_VALUE}>No category</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span>
            {categories.find((c) => c.id === categoryId)?.name ?? "No category"}
          </span>
        )}
      </div>

      {/* Assignee — reassign via the same generic mechanism as
          status/priority/category. Uses the delegate-ticket action under
          the hood (onReassign) so the "reassigned" activity entry and
          notification fire correctly, rather than a raw field update. */}
      <div className="flex items-center gap-1.5">
        <span className="text-muted-foreground">Assigned to</span>
        {editable && onReassign ? (
          <Select
            value={assignedToUserId ?? UNASSIGNED_VALUE}
            onValueChange={(v) => {
              if (v !== UNASSIGNED_VALUE) onReassign(v);
            }}
          >
            <SelectTrigger className="h-7 text-sm w-[160px]" size="sm">
              <SelectValue placeholder="Unassigned" />
            </SelectTrigger>
            <SelectContent>
              {!assignedToUserId && (
                <SelectItem value={UNASSIGNED_VALUE}>Unassigned</SelectItem>
              )}
              {assignableUsers.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="flex items-center gap-1">
            <UserCog className="size-3.5 text-muted-foreground" />
            {assignedToUserId
              ? resolveUserName(assignedToUserId)
              : "Unassigned"}
          </span>
        )}
      </div>

      {/* Tags */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-muted-foreground">Tags</span>
        {tags.map((tag) => (
          <Badge key={tag.id} variant="outline" className="gap-1 pr-1">
            {tag.tag_value}
            {editable && (
              <button
                type="button"
                onClick={() => onRemoveTag(tag.id)}
                className="hover:text-destructive"
                aria-label={`Remove tag ${tag.tag_value}`}
              >
                <X className="size-3" />
              </button>
            )}
          </Badge>
        ))}
        {editable &&
          (showTagInput ? (
            <div className="flex items-center gap-1">
              <Input
                autoFocus
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddTag();
                  if (e.key === "Escape") {
                    setShowTagInput(false);
                    setNewTag("");
                  }
                }}
                placeholder="Tag name"
                className="h-7 text-sm w-[120px]"
              />
              <Button size="sm" className="h-7" onClick={handleAddTag}>
                Add
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowTagInput(true)}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Add tag"
            >
              <Plus className="size-3.5" />
            </button>
          ))}
      </div>

      {/* Watchers — the button's effect (get notified when this ticket
          changes) is stated up front rather than left implicit, and who
          else is watching is named rather than just counted. */}
      <div className="flex items-center gap-1.5 ml-auto">
        <Button
          variant={isWatching ? "secondary" : "outline"}
          size="sm"
          className="h-7 gap-1.5"
          onClick={onToggleWatch}
          disabled={isImplicitWatcher}
          title={
            isImplicitWatcher
              ? "You're the requester — always watching"
              : isWatching
                ? "You'll be notified of changes — click to stop watching"
                : "Get notified when this ticket changes"
          }
        >
          {isWatching ? (
            <Eye className="size-3.5" />
          ) : (
            <EyeOff className="size-3.5" />
          )}
          {isWatching ? "Watching" : "Watch"}
        </Button>
        {otherWatcherNames.length > 0 && (
          <span
            className="text-xs text-muted-foreground"
            title={otherWatcherNames.join(", ")}
          >
            {otherWatcherNames.length === 1
              ? otherWatcherNames[0]
              : `${otherWatcherNames[0]} +${otherWatcherNames.length - 1}`}{" "}
            also watching
          </span>
        )}
      </div>
    </div>
  );
}
